import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
// LA VESTE (cantiere «grafica enrollment», passo 1): gli stessi tre fogli di
// Login.jsx, e per le stesse ragioni — vedi il commento d'importazione lì.
// `Login.css` e `Register.css` sono stati ritirati.
import "../components/HomeShell.css";
import "../components/AdminTools.css";
import "./Enrolment.css";
import { auth } from "../firebase";
import { isSignInWithEmailLink, signInWithEmailLink } from "firebase/auth";
import { CHIAVE_EMAIL_ACCESSO } from "./Login";

// ⚠️ RL-8b — LA PAGINA CHE RICEVE IL LINK.
//
// Ci si atterra cliccando il link ricevuto per email: l'indirizzo porta con
// sé, in query string, `mode=signIn` e `oobCode=…`, che sono l'accesso vero e
// proprio. Per questo la rotta /finish-login in App.jsx è dichiarata NUDA,
// senza PublicRoute: PublicRoute rimanderebbe a /home chi ha già una
// sessione, e quel rimbalzo butterebbe via la query string — cioè il codice —
// prima che questa pagina possa usarlo.
//
// Che cosa NON fa questa pagina, di proposito: non chiede al backend chi sia
// la persona, non guarda ruolo, status o scheda, non decide dove mandarla.
// Al successo va a /home e basta; da lì l'osservatore di AuthContext e il
// cancello (RequireRole) fanno il loro mestiere, che è uno solo e sta in un
// posto solo. Il «che cosa vede chi si autentica senza avere una scheda» è
// materia di RL-8c: qui non se ne anticipa nulla.

export default function FinishLogin() {
  const navigate = useNavigate();

  // "attesa" | "chiedi-email" | "errore"
  const [stato, setStato] = useState("attesa");
  const [email, setEmail] = useState("");
  const [errore, setErrore] = useState("");
  const [loading, setLoading] = useState(false);

  // ⚠️ Guardia contro la SECONDA esecuzione dell'effetto. In sviluppo React
  // gira sotto StrictMode (index.js) e monta ogni componente due volte: senza
  // questa guardia `signInWithEmailLink` verrebbe chiamata due volte con lo
  // stesso `oobCode`, la seconda fallirebbe (il codice è a uso singolo) e si
  // vedrebbe un errore su un accesso in realtà riuscito.
  const giaAvviato = useRef(false);

  const completa = useCallback(
    async (indirizzo) => {
      setStato("attesa");
      setErrore("");
      setLoading(true);
      try {
        await signInWithEmailLink(auth, indirizzo, window.location.href);
        try {
          window.localStorage.removeItem(CHIAVE_EMAIL_ACCESSO);
        } catch (_) {
          /* localStorage non disponibile: niente da pulire. */
        }
        // `replace` toglie dalla cronologia l'indirizzo con dentro il codice.
        navigate("/home", { replace: true });
      } catch (err) {
        setErrore(
          "This sign-in link is not valid any more. It may have expired or " +
            "already been used. Please request a new one."
        );
        setStato("errore");
      } finally {
        setLoading(false);
      }
    },
    [navigate]
  );

  useEffect(() => {
    if (giaAvviato.current) return;
    giaAvviato.current = true;

    // Non è un link d'accesso (qualcuno è arrivato qui digitando l'indirizzo,
    // o il codice è stato tagliato via): non c'è nulla da completare.
    if (!isSignInWithEmailLink(auth, window.location.href)) {
      navigate("/login", { replace: true });
      return;
    }

    let memorizzata = "";
    try {
      memorizzata = window.localStorage.getItem(CHIAVE_EMAIL_ACCESSO) || "";
    } catch (_) {
      memorizzata = "";
    }

    // Link aperto su un altro dispositivo o in un altro browser (il caso
    // normale di chi legge la posta sul telefono): lì il parcheggio è vuoto,
    // e l'unico modo di procedere è farsi ridire l'indirizzo. Firebase lo
    // pretende: il codice da solo non basta.
    if (!memorizzata) {
      setStato("chiedi-email");
      return;
    }

    completa(memorizzata);
  }, [completa, navigate]);

  return (
    <div className="qpsc-page">
      <main className="qpsc-column">
        <header className="qpsc-en-head">
          <img src="/images/logo.png" alt="QPSC Logo" className="qpsc-en-logo" />
          <h2 className="qpsc-page-title">QPSC Login</h2>
        </header>

        {stato === "attesa" && (
          <p className="qpsc-en-msg">Signing you in, please wait...</p>
        )}

        {stato === "chiedi-email" && (
          <form
            className="qpsc-en-form"
            onSubmit={(e) => {
              e.preventDefault();
              const indirizzo = email.trim().toLowerCase();
              if (!indirizzo) return;
              completa(indirizzo);
            }}
          >
            {/* Non è un esito né un errore: è la riga che dice che cosa fare.
                Va quindi in testo principale, non nel verde delle conferme —
                che qui prometteva un successo non ancora avvenuto. */}
            <p className="qpsc-en-msg">
              Please confirm the email address you used to request the link.
            </p>
            <label className="qpsc-field">
              <span className="qpsc-field__label">Email</span>
              <input
                type="email"
                className="qpsc-input"
                placeholder="Email"
                value={email}
                autoComplete="username"
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                required
              />
            </label>
            <button
              className="qpsc-tool-btn qpsc-tool-btn--wide"
              type="submit"
              disabled={loading}
            >
              {loading ? "Signing in..." : "Continue"}
            </button>
          </form>
        )}

        {stato === "errore" && (
          <>
            <p className="qpsc-en-msg qpsc-en-msg--error">{errore}</p>
            <button
              className="qpsc-tool-btn qpsc-tool-btn--wide"
              type="button"
              onClick={() => navigate("/login", { replace: true })}
            >
              Back to login
            </button>
          </>
        )}

        <div className="qpsc-en-links">
          <a href="/enrol" className="qpsc-en-link">Not enrolled yet? ENROL NOW</a>
        </div>
      </main>
    </div>
  );
}
